// DECKS — the proposal-deck generator's operator surface.
//
// Without a `deck` arrival intent this is the engagement's deck list (cards, «New deck»);
// with one it is the EDITOR: slide rail left (scaled live previews, include-toggle, position
// arrows), the active slide large in the middle, and the slot panel right — one input per
// content slot with its hard character budget, red on overflow, saved by PATCH on blur.
//
// THE ONE LAW OF THIS PAGE: nobody edits geometry here. The design is frozen (golden master
// LCH01 v15); the operator owns prose SLOTS, the register owns the bound data (deliverables,
// requirements, dates — polled every 10s from /api/decks/:id/bound and rendered read-only),
// and the shared validateSlideContent guards every budget before anything is persisted.
// Below the slots sits the FieldChat — the same draft-with-the-agent flap the register forms
// use, its fields wired to the active slide's text slots.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useEngagement } from '../../context/EngagementContext.js';
import ConfirmModal from '../../components/ConfirmModal.js';
import EmptyState from '../../components/EmptyState.js';
import FieldChat, { type ChatField } from '../../components/FieldChat.js';
import { PageHeader, PageShell, Sep, Sheet, Zone, ZoneHead } from '../../components/PageKit.js';
import { useToast } from '../../components/Toast.js';
import { usePoll } from '../../hooks/usePoll.js';
import { useDeepLink, useRouteParams } from '../../routes.js';
import { ApiError } from '../../api/client.js';
import {
  decksApi, type DeckBoundMap, type DeckDetail, type DeckImageLibrary, type DeckPerson,
  type DeckSlide, type DeckVersion, type MandateReference, type SlideBound,
} from '../../api/decks.js';
import {
  DECK_IMAGE_CHOICES, SLIDE_TYPE_BY_ID, SLIDE_TYPE_CATALOG, imageFrameOf, validateSlideContent,
  type DeckSlideType, type SlideTypeDef, type SlotDef, type SlotViolation,
} from '../../../shared/deckTypes.js';
import { useLocale, useT } from '../../i18n/index.js';
import SlidePreview, { slidePageCount } from './slides/SlidePreview.js';

import './DecksPage.de.js';
import './DecksPage.fr.js';

/**
 * The FieldChat style directive — VERBATIM from the approved seed sheet (deck-spec SEEDS.md).
 * Deliberately German and deliberately not translated: it instructs the agent about the
 * document voice, which is the deck's, not the operator UI's.
 */
const STYLE_DIRECTIVE =
  'Ton: trocken, klar und zielgerichtet; Schweizer Standarddeutsch (ss statt ß). '
  + 'Terminologie: ‹Arbeitsergebnis› (nie Lieferobjekt), ‹Abnahmekriterien› (nie Definition of Done). '
  + 'Keine dramatischen Formulierungen, keine Aufzählungs-Floskeln, kein aus dem Englischen '
  + 'übersetzt klingendes Deutsch. Zeichenbudgets sind hart — Vorschläge müssen ins Budget passen.';

const BOUND_POLL_MS = 10_000;
const LIST_POLL_MS = 20_000;

// ─────────────────────────────────────────────────────────────────────────────
// Page root: list without a deck, editor with one
// ─────────────────────────────────────────────────────────────────────────────

export default function DecksPage() {
  const t = useT();
  const { current } = useEngagement();
  const [deckId, setDeckId] = useState<number | null>(null);

  // F1 receiver: `deck` opens the editor directly (one-shot, pattern ProposalsPage).
  const params = useRouteParams();
  useDeepLink(params, {
    deck: (v) => { const n = Number(v); if (Number.isInteger(n) && n > 0) setDeckId(n); },
  });

  if (!current) {
    return (
      <PageShell>
        <PageHeader title={t('Decks')} />
        <EmptyState
          kind="empty"
          title={t('No engagement selected')}
          detail={t('Decks belong to one engagement — pick an engagement to see its proposal decks.')}
        />
      </PageShell>
    );
  }

  return deckId === null
    ? <DeckList engagementId={current.id} onOpen={setDeckId} />
    : <DeckEditor engagementId={current.id} deckId={deckId} onBack={() => setDeckId(null)} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// The list
// ─────────────────────────────────────────────────────────────────────────────

function DeckList({ engagementId, onOpen }: {
  engagementId: number;
  onOpen: (id: number) => void;
}) {
  const t = useT();
  const { fmtDateTime } = useLocale();
  const toast = useToast();

  const load = useCallback(() => decksApi.list(engagementId), [engagementId]);
  const { data, loading, error, notice, refresh } = usePoll(load, LIST_POLL_MS);
  const decks = data ?? [];

  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const create = useCallback(async () => {
    const name = title.trim();
    if (name.length < 2 || busy) return;
    setBusy(true);
    try {
      const deck = await decksApi.create(engagementId, name);
      onOpen(deck.id);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The deck could not be created.'));
    } finally {
      setBusy(false);
    }
  }, [title, busy, engagementId, onOpen, toast, t]);

  return (
    <PageShell>
      <PageHeader
        title={t('Decks')}
        sub={notice ?? undefined}
        verbs={
          <button type="button" className="btn btn-accent" onClick={() => setCreating(true)}>
            {t('New deck')}
          </button>
        }
      />
      <Zone>
        <ZoneHead label={t('Decks')} count={decks.length || undefined} />
        {creating ? (
          <Sheet pad style={{ marginBottom: 12 }}>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <label className="form-label">{t('Deck title')}</label>
              <input
                className="form-input"
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void create(); }}
                placeholder={t('e.g. “Vorprojekt — Projektplan & Angebot”')}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-accent" disabled={busy || title.trim().length < 2} onClick={() => void create()}>
                {busy ? <span className="spinner spinner-sm" /> : null} {t('Create')}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => setCreating(false)}>
                {t('Cancel')}
              </button>
            </div>
            <p className="dke-bound-note" style={{ marginTop: 8 }}>
              {t('A new deck starts with the standard 13-slide order — every slide can be toggled off in the editor.')}
            </p>
          </Sheet>
        ) : null}
        {loading && decks.length === 0 ? (
          <Sheet pad><span className="spinner spinner-sm" /></Sheet>
        ) : error && decks.length === 0 ? (
          <EmptyState kind="error" title={t('Could not load the decks')} detail={error} onRetry={() => void refresh()} />
        ) : decks.length === 0 ? (
          <EmptyState
            kind="empty"
            title={t('No decks yet')}
            detail={t('A deck is this engagement’s proposal document — slides bound live to the register.')}
          >
            <button type="button" className="btn btn-accent" onClick={() => setCreating(true)}>
              {t('New deck')}
            </button>
          </EmptyState>
        ) : (
          <div className="dke-cards">
            {decks.map((d) => (
              <button key={d.id} type="button" className="dke-card" onClick={() => onOpen(d.id)}>
                <span className="t">{d.title}</span>
                <span className="meta">
                  <span className="mono">{t(d.status)}</span>
                  <span className="mono">{d.language.toUpperCase()}</span>
                  {d.included_slides !== undefined ? <span>{t('{n} slides', { n: d.included_slides })}</span> : null}
                  {d.latest_version_no ? <span className="mono">v{d.latest_version_no}</span> : null}
                  <span>{fmtDateTime(d.updated_at)}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </Zone>
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The editor
// ─────────────────────────────────────────────────────────────────────────────

/** Measure a container's inner width (the stage scales the 1280px slide into it). */
function useMeasuredWidth<T extends HTMLElement>(): { ref: (el: T | null) => void; width: number } {
  const [width, setWidth] = useState(0);
  const roRef = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: T | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    roRef.current = ro;
    setWidth(el.clientWidth);
  }, []);
  return { ref, width };
}

function DeckEditor({ engagementId, deckId, onBack }: {
  engagementId: number;
  deckId: number;
  onBack: () => void;
}) {
  const t = useT();
  const toast = useToast();

  // ── the deck and its slides ────────────────────────────────────────────────
  const [detail, setDetail] = useState<DeckDetail | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const loadDetail = useCallback(async () => {
    try {
      const d = await decksApi.get(deckId);
      d.slides.sort((a, b) => a.position - b.position || a.id - b.id);
      setDetail(d);
      setLoadErr(null);
    } catch (err) {
      setLoadErr(err instanceof Error ? err.message : String(err));
    }
  }, [deckId]);
  useEffect(() => { void loadDetail(); }, [loadDetail]);

  const slides = useMemo(() => detail?.slides ?? [], [detail]);

  // ── the live register data — ONE fetch for all bound slides, polled ────────
  const loadBound = useCallback(() => decksApi.bound(deckId), [deckId]);
  const boundPoll = usePoll<DeckBoundMap>(loadBound, BOUND_POLL_MS);
  const boundMap = boundPoll.data;
  const boundFor = useCallback(
    (slide: DeckSlide): SlideBound | undefined => boundMap?.[String(slide.id)],
    [boundMap],
  );

  // ── the pickers' libraries (people, mandate references) ────────────────────
  const [people, setPeople] = useState<DeckPerson[]>([]);
  const [references, setReferences] = useState<MandateReference[]>([]);
  const [images, setImages] = useState<DeckImageLibrary>({ uploaded: [], hidden: [] });
  const [confirmRemove, setConfirmRemove] = useState<DeckSlide | null>(null);
  const [addType, setAddType] = useState<DeckSlideType>('anhang_divider');
  const loadImages = useCallback(() => decksApi.images().then(setImages).catch(() => setImages({ uploaded: [], hidden: [] })), []);
  useEffect(() => {
    decksApi.people().then(setPeople).catch(() => setPeople([]));
    decksApi.mandateReferences().then(setReferences).catch(() => setReferences([]));
    void loadImages();
  }, []);

  // ── versions / export ──────────────────────────────────────────────────────
  const [versions, setVersions] = useState<DeckVersion[]>([]);
  useEffect(() => {
    decksApi.versions(deckId).then(setVersions).catch(() => setVersions([]));
  }, [deckId]);
  const [exporting, setExporting] = useState(false);
  const doExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const v = await decksApi.export(deckId);
      setVersions((vs) => [v, ...vs]);
      toast('ok', t('Version v{n} exported', { n: v.version_no }));
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The export failed — nothing was written.'));
    } finally {
      setExporting(false);
    }
  }, [deckId, exporting, toast, t]);
  const latestVersion = versions[0] ?? null;

  // ── the active slide and its draft content ─────────────────────────────────
  const [activeId, setActiveId] = useState<number | null>(null);
  const [activePage, setActivePage] = useState(0);
  const active = slides.find((s) => s.id === activeId) ?? null;
  useEffect(() => {
    if (activeId === null && slides.length > 0) setActiveId(slides[0].id);
  }, [activeId, slides]);

  const [draft, setDraftState] = useState<Record<string, unknown>>({});
  const draftRef = useRef(draft);
  const setDraft = useCallback((next: Record<string, unknown>) => {
    draftRef.current = next;
    setDraftState(next);
  }, []);
  const [violations, setViolations] = useState<SlotViolation[]>([]);

  // a fresh slide selection re-seeds the draft from the record
  useEffect(() => {
    if (!active) return;
    setDraft({ ...active.content });
    setViolations([]);
    setActivePage(0);
    // the draft follows the SELECTION, not every slides refresh — local edits survive polls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  /** Validate and persist one draft state. Over-budget content stays local, marked red. */
  const save = useCallback(async (next: Record<string, unknown>) => {
    const slide = slides.find((s) => s.id === activeId);
    if (!slide) return;
    const v = validateSlideContent(slide.slide_type, next);
    setViolations(v.violations);
    if (!v.ok) return;
    try {
      const updated = await decksApi.updateSlide(deckId, slide.id, { content: v.cleaned });
      setDetail((d) => d
        ? { ...d, slides: d.slides.map((s) => (s.id === updated.id ? updated : s)) }
        : d);
    } catch (err) {
      if (err instanceof ApiError && Array.isArray((err.body as { violations?: SlotViolation[] }).violations)) {
        setViolations((err.body as { violations: SlotViolation[] }).violations);
      } else {
        toast('err', err instanceof Error ? err.message : t('The slide could not be saved.'));
      }
    }
  }, [slides, activeId, deckId, toast, t]);

  const setSlot = useCallback((key: string, value: unknown) => {
    const next = { ...draftRef.current };
    if (value === undefined) delete next[key];
    else next[key] = value;
    setDraft(next);
  }, [setDraft]);

  /** Set + persist in one step (image picks, ref picks, FieldChat takes). */
  const setSlotAndSave = useCallback((key: string, value: unknown) => {
    const next = { ...draftRef.current };
    if (value === undefined) delete next[key];
    else next[key] = value;
    setDraft(next);
    void save(next);
  }, [setDraft, save]);

  const saveDraft = useCallback(() => { void save(draftRef.current); }, [save]);

  // ── slide verbs: include, move ─────────────────────────────────────────────
  const toggleIncluded = useCallback(async (slide: DeckSlide) => {
    try {
      const updated = await decksApi.updateSlide(deckId, slide.id, { included: !slide.included });
      setDetail((d) => d
        ? { ...d, slides: d.slides.map((s) => (s.id === updated.id ? updated : s)) }
        : d);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The slide could not be saved.'));
    }
  }, [deckId, toast, t]);

  const move = useCallback(async (slide: DeckSlide, dir: -1 | 1) => {
    const idx = slides.findIndex((s) => s.id === slide.id);
    const other = slides[idx + dir];
    if (!other) return;
    try {
      await decksApi.updateSlide(deckId, slide.id, { position: other.position });
      await decksApi.updateSlide(deckId, other.id, { position: slide.position });
      await loadDetail();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The slide could not be saved.'));
      await loadDetail();
    }
  }, [slides, deckId, loadDetail, toast, t]);

  /** Add a slide of any catalogue type right after the active one (or at the end). */
  const addSlide = useCallback(async () => {
    try {
      const created = await decksApi.addSlide(deckId, addType, activeId);
      await loadDetail();
      setActiveId(created.id);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The slide could not be saved.'));
    }
  }, [deckId, addType, activeId, loadDetail, toast, t]);

  const removeSlide = useCallback(async (slide: DeckSlide) => {
    setConfirmRemove(null);
    try {
      await decksApi.removeSlide(deckId, slide.id);
      const idx = slides.findIndex((s) => s.id === slide.id);
      const neighbour = slides[idx + 1] ?? slides[idx - 1] ?? null;
      await loadDetail();
      if (neighbour && activeId === slide.id) setActiveId(neighbour.id);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The slide could not be saved.'));
    }
  }, [deckId, slides, activeId, loadDetail, toast, t]);

  /** Upload an image into the library and pick it for the slot straight away. */
  const uploadImage = useCallback(async (slotKey: string, file: File) => {
    try {
      const img = await decksApi.uploadImage(file);
      await loadImages();
      setSlotAndSave(slotKey, img.key);
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The image could not be uploaded.'));
    }
  }, [loadImages, setSlotAndSave, toast, t]);

  /** «Delete» on a picker image: uploaded files go for good, shipped ones are hidden. */
  const deleteImage = useCallback(async (key: string) => {
    try {
      if (key.startsWith('/deck-images/')) await decksApi.deleteImage(key);
      else await decksApi.hideImage(key);
      await loadImages();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The image could not be deleted.'));
    }
  }, [loadImages, toast, t]);

  const restoreImage = useCallback(async (key: string) => {
    try {
      await decksApi.unhideImage(key);
      await loadImages();
    } catch (err) {
      toast('err', err instanceof Error ? err.message : t('The image could not be restored.'));
    }
  }, [loadImages, toast, t]);

  // ── printed page numbers over the included slides (paginated slides span several) ──
  const pageNumbers = useMemo(() => {
    const map = new Map<number, number>();
    let n = 0;
    for (const s of slides) {
      if (!s.included) continue;
      map.set(s.id, n + 1);
      n += slidePageCount(s, boundMap?.[String(s.id)]);
    }
    return map;
  }, [slides, boundMap]);

  // ── the stage's fit-to-width scale ─────────────────────────────────────────
  const stage = useMeasuredWidth<HTMLDivElement>();
  const scale = stage.width > 0 ? Math.min(1, (stage.width - 8) / 1280) : 0.5;

  // ── FieldChat wiring: the active slide's plain text slots ──────────────────
  const def: SlideTypeDef | null = active ? SLIDE_TYPE_BY_ID[active.slide_type] : null;
  const textSlots = useMemo(
    () => (def ? def.slots.filter((s) => s.kind === 'text' && !s.dynamic) : []),
    [def],
  );
  const chatFields = useMemo<ChatField[]>(() => textSlots.map((s) => ({
    key: s.key,
    label: t(s.label),
    get: () => String(draftRef.current[s.key] ?? ''),
    set: (text) => setSlotAndSave(s.key, text),
  })), [textSlots, setSlotAndSave, t]);
  const chatContext = useMemo(() => (def ? [
    `Slide type: ${def.name} (${def.type}). The design is frozen — only these content slots exist.`,
    `Hard character budgets: ${textSlots.map((s) => `${s.key} ≤ ${s.maxChars ?? '∞'}`).join(', ')}.`,
    `Style directive: ${STYLE_DIRECTIVE}`,
  ] : []), [def, textSlots]);

  // ── page states ────────────────────────────────────────────────────────────
  if (loadErr && !detail) {
    return (
      <PageShell>
        <PageHeader title={t('Decks')} />
        <EmptyState kind="error" title={t('Could not load the deck')} detail={loadErr} onRetry={() => void loadDetail()} />
      </PageShell>
    );
  }
  if (detail && slides.length === 0) {
    return (
      <PageShell>
        <PageHeader title={detail.deck.title} verbs={
          <button type="button" className="btn btn-ghost" onClick={onBack}>{t('Back to decks')}</button>
        } />
        <EmptyState
          kind="empty"
          title={t('No slides in this deck')}
          detail={t('A deck is created with the standard 13-slide order — this one has none.')}
        />
      </PageShell>
    );
  }
  if (!detail || !active || !def) {
    return (
      <PageShell>
        <PageHeader title={t('Decks')} />
        <Sheet pad><span className="spinner spinner-sm" /></Sheet>
      </PageShell>
    );
  }

  const deck = detail.deck;
  const pageCount = slidePageCount(active, boundFor(active));

  return (
    <PageShell>
      <PageHeader
        title={deck.title}
        sub={
          <>
            <span className="mono">{t(deck.status)}</span>
            <Sep />
            <span className="mono">{deck.language.toUpperCase()}</span>
            <Sep />
            {t('{n} slides', { n: slides.length })}
            {boundPoll.notice ? (<><Sep />{boundPoll.notice}</>) : null}
          </>
        }
        verbs={
          <>
            {latestVersion?.pptx_key ? (
              <a
                className="btn btn-ghost"
                href={decksApi.versionDownloadUrl(latestVersion.id)}
                download
              >
                {t('Download v{n}', { n: latestVersion.version_no })}
              </a>
            ) : null}
            <button type="button" className="btn btn-accent" disabled={exporting} onClick={() => void doExport()}>
              {exporting ? <span className="spinner spinner-sm" /> : null} {t('Export PPTX')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={onBack}>
              {t('Back to decks')}
            </button>
          </>
        }
      />

      <ConfirmModal
        open={!!confirmRemove}
        title={t('Remove slide')}
        confirmLabel={t('Remove')}
        danger
        onConfirm={() => { if (confirmRemove) void removeSlide(confirmRemove); }}
        onCancel={() => setConfirmRemove(null)}
      >
        {confirmRemove ? t('Remove «{name}» from this deck? Its slot texts are lost; the register data stays.', { name: t(SLIDE_TYPE_BY_ID[confirmRemove.slide_type].name) }) : null}
      </ConfirmModal>

      <div className="dke">
        {/* ── the slide rail ── */}
        <div className="dke-rail">
          {slides.map((s, idx) => (
            <div
              key={s.id}
              className={`dke-thumb${s.id === active.id ? ' on' : ''}${s.included ? '' : ' dke-thumb--off'}`}
              role="button"
              tabIndex={0}
              onClick={() => setActiveId(s.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveId(s.id); }}
            >
              <div className="dke-thumb-frame">
                <SlidePreview slide={s} bound={boundFor(s)} pageNo={pageNumbers.get(s.id) ?? null} />
              </div>
              <div className="dke-thumb-meta">
                <span className="pos mono">{idx + 1}</span>
                <span className="nm">{t(SLIDE_TYPE_BY_ID[s.slide_type].name)}</span>
                <span className="dke-thumb-verbs" onClick={(e) => e.stopPropagation()}>
                  <button type="button" title={t('Move up')} disabled={idx === 0} onClick={() => void move(s, -1)}>↑</button>
                  <button type="button" title={t('Move down')} disabled={idx === slides.length - 1} onClick={() => void move(s, 1)}>↓</button>
                  <input
                    type="checkbox"
                    checked={s.included}
                    title={t('Included')}
                    onChange={() => void toggleIncluded(s)}
                  />
                  <button type="button" title={t('Remove slide')} disabled={slides.length <= 1} onClick={() => setConfirmRemove(s)}>×</button>
                </span>
              </div>
            </div>
          ))}
          {/* add a slide of any type after the active one */}
          <div className="dke-add">
            <select className="form-select" value={addType} onChange={(e) => setAddType(e.target.value as DeckSlideType)}>
              {SLIDE_TYPE_CATALOG.map((d) => <option key={d.type} value={d.type}>{t(d.name)}</option>)}
            </select>
            <button type="button" className="btn btn-sm" onClick={() => void addSlide()}>{t('Add after this slide')}</button>
          </div>
        </div>

        {/* ── the stage: the active slide at fit-to-width scale ── */}
        <div className="dke-stage-col">
          <div ref={stage.ref} className="dke-stage">
            {Array.from({ length: pageCount }, (_, p) => (
              <div
                key={p}
                className="dke-stage-fit"
                style={{ width: 1280 * scale, height: 720 * scale, marginBottom: p < pageCount - 1 ? 14 : 0 }}
              >
                <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                  <SlidePreview
                    slide={{ ...active, content: draft }}
                    bound={boundFor(active)}
                    page={p}
                    pageNo={pageNumbers.has(active.id) ? (pageNumbers.get(active.id) as number) + p : null}
                  />
                </div>
              </div>
            ))}
          </div>
          {pageCount > 1 ? (
            <span className="dke-bound-note">{t('This slide paginates into {n} pages from the register data.', { n: pageCount })}</span>
          ) : null}
        </div>

        {/* ── the slot panel ── */}
        <div className="dke-panel">
          <ZoneHead label={t('Slots')} note={t(def.name)} />

          {def.bound?.length ? (
            <BoundInfo bound={boundFor(active)} loading={boundPoll.loading} />
          ) : null}

          {def.slots.filter((slot) => !slot.frameOf).map((slot) => (
            <SlotEditor
              key={slot.key}
              slot={slot}
              slide={active}
              draft={draft}
              violations={violations}
              people={people}
              references={references}
              bound={boundFor(active)}
              images={images}
              onUploadImage={uploadImage}
              onDeleteImage={deleteImage}
              onRestoreImage={restoreImage}
              setSlot={setSlot}
              setSlotAndSave={setSlotAndSave}
              saveDraft={saveDraft}
            />
          ))}

          {violations.length > 0 ? (
            <div className="dke-violation">{t('Over budget — not saved. Shorten the marked slots.')}</div>
          ) : null}

          <div className="dke-chat">
            <FieldChat
              key={active.id}
              engagementId={engagementId}
              title={`${t(def.name)} — ${deck.title}`}
              contextLines={chatContext}
              fields={chatFields}
            />
          </div>
        </div>
      </div>
    </PageShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Slot editors
// ─────────────────────────────────────────────────────────────────────────────

function violationFor(violations: SlotViolation[], key: string): SlotViolation | undefined {
  return violations.find((v) => v.slot === key || v.slot.startsWith(`${key}[`) || v.slot.startsWith(`${key}.`));
}

function SlotHead({ label, count, max, over }: {
  label: string; count?: number; max?: number; over?: boolean;
}) {
  return (
    <div className="dke-slot-head">
      <label className="form-label">{label}</label>
      {max !== undefined ? (
        <span className={`dke-count${over ? ' over' : ''}`}>{count ?? 0}/{max}</span>
      ) : null}
    </div>
  );
}

function SlotEditor({
  slot, slide, draft, violations, people, references, bound, images, onUploadImage, onDeleteImage, onRestoreImage,
  setSlot, setSlotAndSave, saveDraft,
}: {
  slot: SlotDef;
  slide: DeckSlide;
  draft: Record<string, unknown>;
  violations: SlotViolation[];
  people: DeckPerson[];
  references: MandateReference[];
  bound: SlideBound | undefined;
  images: DeckImageLibrary;
  onUploadImage: (slotKey: string, file: File) => void;
  onDeleteImage: (key: string) => void;
  onRestoreImage: (key: string) => void;
  setSlot: (key: string, value: unknown) => void;
  setSlotAndSave: (key: string, value: unknown) => void;
  saveDraft: () => void;
}) {
  const t = useT();

  // dynamic slots (per-row overrides) are driven by the bound rows/cards
  if (slot.dynamic) {
    return (
      <DynamicOverrides
        slot={slot} slide={slide} draft={draft} violations={violations} bound={bound}
        setSlot={setSlot} saveDraft={saveDraft}
      />
    );
  }

  const vio = violationFor(violations, slot.key);

  switch (slot.kind) {
    case 'text': {
      const value = String(draft[slot.key] ?? '');
      const over = slot.maxChars !== undefined && value.trim().length > slot.maxChars;
      const long = (slot.maxChars ?? 0) > 80;
      return (
        <div className="dke-slot">
          <SlotHead label={t(slot.label)} count={value.trim().length} max={slot.maxChars} over={over} />
          {long ? (
            <textarea
              className={`form-textarea${over ? ' dke-over' : ''}`}
              rows={Math.min(6, Math.max(2, Math.ceil((slot.maxChars ?? 120) / 70)))}
              value={value}
              onChange={(e) => setSlot(slot.key, e.target.value)}
              onBlur={saveDraft}
            />
          ) : (
            <input
              className={`form-input${over ? ' dke-over' : ''}`}
              value={value}
              onChange={(e) => setSlot(slot.key, e.target.value)}
              onBlur={saveDraft}
            />
          )}
          {vio ? <span className="dke-violation">{vio.message}</span> : null}
        </div>
      );
    }

    case 'int':
    case 'number': {
      const raw = draft[slot.key];
      const value = raw === undefined || raw === null ? '' : String(raw);
      if (slot.key === 'split_x') {
        // The split edge: a slider with the live preview following every step, the number
        // beside it for precision. 420–900 px of the 1280 width, default per slide type.
        const fallback = slide.slide_type === 'auftrag_split' ? 755 : 640;
        const cur = typeof raw === 'number' ? raw : fallback;
        return (
          <div className="dke-slot">
            <SlotHead label={t(slot.label)} />
            <div className="dke-framer-row">
              <input
                type="range" min={420} max={900} step={5} value={cur}
                onChange={(e) => setSlot(slot.key, Number(e.target.value))}
                onPointerUp={saveDraft}
                onKeyUp={saveDraft}
              />
              <input
                className="form-input mono" type="number" min={420} max={900} step={5} value={cur}
                style={{ width: 84 }}
                onChange={(e) => { const v = e.target.value; if (v !== '') setSlot(slot.key, Number(v)); }}
                onBlur={saveDraft}
              />
              <span className="dke-hint">px</span>
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setSlot(slot.key, undefined); saveDraft(); }}>{t('Reset')}</button>
            </div>
            <span className="dke-hint">{t('The image starts here; the text column ends 36 px before it.')}</span>
            {vio ? <span className="dke-violation">{vio.message}</span> : null}
          </div>
        );
      }
      return (
        <div className="dke-slot">
          <SlotHead label={t(slot.label)} />
          <input
            className="form-input"
            type="number"
            step={slot.kind === 'int' ? 1 : 'any'}
            min={0}
            value={value}
            onChange={(e) => {
              const v = e.target.value;
              setSlot(slot.key, v === '' ? undefined : Number(v));
            }}
            onBlur={saveDraft}
          />
          {vio ? <span className="dke-violation">{vio.message}</span> : null}
        </div>
      );
    }

    case 'lines': {
      const lines = Array.isArray(draft[slot.key]) ? (draft[slot.key] as string[]) : [];
      const text = lines.join('\n');
      const worst = lines.reduce((m, l) => Math.max(m, l.trim().length), 0);
      const over = slot.maxChars !== undefined && worst > slot.maxChars;
      return (
        <div className="dke-slot">
          <SlotHead label={t(slot.label)} count={worst} max={slot.maxChars} over={over} />
          <textarea
            className={`form-textarea${over ? ' dke-over' : ''}`}
            rows={Math.max(2, Math.min(slot.maxItems ?? 4, 6))}
            value={text}
            placeholder={t('One entry per line')}
            onChange={(e) => setSlot(slot.key, e.target.value.split('\n'))}
            onBlur={() => {
              setSlotAndSave(slot.key, lines.map((l) => l.trim()).filter((l) => l !== ''));
            }}
          />
          {vio ? <span className="dke-violation">{vio.message}</span> : null}
        </div>
      );
    }

    case 'image': {
      const value = String(draft[slot.key] ?? '');
      const hidden = new Set(images.hidden);
      const choices = (slot.choices ?? DECK_IMAGE_CHOICES).filter((src) => !hidden.has(src));
      const uploaded = images.uploaded.filter((img) => !hidden.has(img.key));
      return (
        <div className="dke-slot">
          <SlotHead label={t(slot.label)} />
          <div className="dke-imgpick">
            <button
              type="button"
              className={value === '' ? 'on' : ''}
              title={t('No image')}
              onClick={() => setSlotAndSave(slot.key, '')}
            >
              <span style={{ display: 'inline-flex', width: 74, height: 42, alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>—</span>
            </button>
            {choices.map((src) => (
              <span key={src} className="dke-imgup">
                <button
                  type="button"
                  className={value === src ? 'on' : ''}
                  title={src}
                  onClick={() => setSlotAndSave(slot.key, src)}
                >
                  <img src={src} alt="" />
                </button>
                <button
                  type="button"
                  className="dke-imgdel"
                  title={t('Remove this image from the picker')}
                  onClick={() => onDeleteImage(src)}
                >×</button>
              </span>
            ))}
            {uploaded.map((img) => (
              <span key={img.key} className="dke-imgup">
                <button
                  type="button"
                  className={value === img.key ? 'on' : ''}
                  title={img.key}
                  onClick={() => setSlotAndSave(slot.key, img.key)}
                >
                  <img src={img.url} alt="" />
                </button>
                <button
                  type="button"
                  className="dke-imgdel"
                  title={t('Delete this image')}
                  onClick={() => onDeleteImage(img.key)}
                >×</button>
              </span>
            ))}
            <label className="dke-imgadd" title={t('Upload an image (PNG or JPEG)')}>
              <input
                type="file" accept="image/png,image/jpeg" hidden
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUploadImage(slot.key, f); }}
              />
              <span>+</span>
            </label>
          </div>
          {images.hidden.length > 0 ? (
            <div className="dke-hidden">
              <span className="dke-hint">{t('{n} hidden', { n: images.hidden.length })}</span>
              {images.hidden.map((key) => (
                <button key={key} type="button" className="dke-hidden-chip" title={key} onClick={() => onRestoreImage(key)}>
                  {key.replace(/^.*\//, '')} ↺
                </button>
              ))}
            </div>
          ) : null}
          {value !== '' ? (
            <ImageFramer
              src={value}
              slotKey={slot.key}
              draft={draft}
              aspect={frameAspectFor(slide, slot.key, draft)}
              onChange={(fx, fy, zoom) => {
                setSlot(`${slot.key}_fx`, fx);
                setSlot(`${slot.key}_fy`, fy);
                setSlot(`${slot.key}_zoom`, zoom);
              }}
              onCommit={saveDraft}
            />
          ) : null}
          {vio ? <span className="dke-violation">{vio.message}</span> : null}
        </div>
      );
    }

    case 'ref': {
      if (slot.key === 'reference_ids') {
        const selected = Array.isArray(draft[slot.key]) ? (draft[slot.key] as number[]) : [];
        const toggle = (id: number) => {
          const next = selected.includes(id)
            ? selected.filter((x) => x !== id)
            : [...selected, id].slice(0, slot.maxItems ?? 3);
          setSlotAndSave(slot.key, next);
        };
        return (
          <div className="dke-slot">
            <SlotHead label={t(slot.label)} count={selected.length} max={slot.maxItems} over={false} />
            <div className="dke-refpick">
              {references.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className={selected.includes(r.id) ? 'on' : ''}
                  onClick={() => toggle(r.id)}
                >
                  <span className="slug">{r.slug}</span>
                  <span className="claim">{r.claim}</span>
                </button>
              ))}
              {references.length === 0 ? (
                <span className="dke-bound-note">{t('No mandate references in the library yet.')}</span>
              ) : null}
            </div>
            {vio ? <span className="dke-violation">{vio.message}</span> : null}
          </div>
        );
      }
      // free ref list (highlight_reqs): comma-separated refs
      const refs = Array.isArray(draft[slot.key]) ? (draft[slot.key] as string[]) : [];
      return (
        <div className="dke-slot">
          <SlotHead label={t(slot.label)} />
          <input
            className="form-input mono"
            value={refs.join(', ')}
            placeholder={t('Refs, comma-separated — e.g. REQ-050')}
            onChange={(e) => setSlot(slot.key, e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
            onBlur={saveDraft}
          />
          {vio ? <span className="dke-violation">{vio.message}</span> : null}
        </div>
      );
    }

    case 'choice': {
      const value = String(draft[slot.key] ?? '');
      return (
        <div className="dke-slot">
          <SlotHead label={t(slot.label)} />
          <select
            className="form-input"
            value={value}
            onChange={(e) => setSlotAndSave(slot.key, e.target.value)}
          >
            <option value="">—</option>
            {(slot.choices ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {vio ? <span className="dke-violation">{vio.message}</span> : null}
        </div>
      );
    }

    case 'items':
      return (
        <ItemsEditor
          slot={slot} draft={draft} violations={violations} people={people}
          setSlot={setSlot} saveDraft={saveDraft}
        />
      );
  }
}

function ItemsEditor({ slot, draft, violations, people, setSlot, saveDraft }: {
  slot: SlotDef;
  draft: Record<string, unknown>;
  violations: SlotViolation[];
  people: DeckPerson[];
  setSlot: (key: string, value: unknown) => void;
  saveDraft: () => void;
}) {
  const t = useT();
  const entries = Array.isArray(draft[slot.key]) ? (draft[slot.key] as Record<string, unknown>[]) : [];
  const fields = slot.itemFields ?? [];
  const vio = violationFor(violations, slot.key);

  const update = (i: number, field: string, value: unknown) => {
    const next = entries.map((e, j) => (j === i ? { ...e, [field]: value } : e));
    setSlot(slot.key, next);
  };
  const add = () => setSlot(slot.key, [...entries, {}]);
  const remove = (i: number) => {
    setSlot(slot.key, entries.filter((_, j) => j !== i));
    // removal is a click, not a blur — persist right away
    queueMicrotask(saveDraft);
  };

  return (
    <div className="dke-slot">
      <SlotHead label={t(slot.label)} count={entries.length} max={slot.maxItems} over={false} />
      {entries.map((entry, i) => (
        <div className="dke-item" key={i}>
          <div className="dke-item-head">
            <span className="u-label">{i + 1}</span>
            <button type="button" className="x" title={t('Remove')} onClick={() => remove(i)}>×</button>
          </div>
          {fields.map((f) => {
            const raw = entry[f.key];
            if (f.kind !== 'text') {
              // person_id gets the people picker; other numbers a plain number input
              if (slot.key === 'person_slots' && f.key === 'person_id') {
                return (
                  <div key={f.key} className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">{t(f.label)}</label>
                    <select
                      className="form-input"
                      value={raw === undefined || raw === null ? '' : String(raw)}
                      onChange={(e) => update(i, f.key, e.target.value === '' ? undefined : Number(e.target.value))}
                      onBlur={saveDraft}
                    >
                      <option value="">—</option>
                      {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                );
              }
              return (
                <div key={f.key} className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">{t(f.label)}</label>
                  <input
                    className="form-input"
                    type="number"
                    step={f.kind === 'int' ? 1 : 'any'}
                    value={raw === undefined || raw === null ? '' : String(raw)}
                    onChange={(e) => update(i, f.key, e.target.value === '' ? undefined : Number(e.target.value))}
                    onBlur={saveDraft}
                  />
                </div>
              );
            }
            const text = String(raw ?? '');
            // profile_context picks from the chosen person's library profiles when it has any
            if (slot.key === 'person_slots' && f.key === 'profile_context') {
              const person = people.find((p) => p.id === entry.person_id);
              const profiles = person?.profiles ?? [];
              if (profiles.length > 0) {
                return (
                  <div key={f.key} className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">{t(f.label)}</label>
                    <select
                      className="form-input"
                      value={text}
                      onChange={(e) => update(i, f.key, e.target.value === '' ? undefined : e.target.value)}
                      onBlur={saveDraft}
                    >
                      <option value="">—</option>
                      {profiles.map((pr) => (
                        <option key={pr.context_key} value={pr.context_key}>{pr.context_key}</option>
                      ))}
                    </select>
                  </div>
                );
              }
            }
            const over = f.maxChars !== undefined && text.trim().length > f.maxChars;
            return (
              <div key={f.key} className="dke-slot">
                <SlotHead label={t(f.label)} count={text.trim().length} max={f.maxChars} over={over} />
                {(f.maxChars ?? 0) > 80 ? (
                  <textarea
                    className={`form-textarea${over ? ' dke-over' : ''}`}
                    rows={2}
                    value={text}
                    onChange={(e) => update(i, f.key, e.target.value)}
                    onBlur={saveDraft}
                  />
                ) : (
                  <input
                    className={`form-input${over ? ' dke-over' : ''}`}
                    value={text}
                    onChange={(e) => update(i, f.key, e.target.value)}
                    onBlur={saveDraft}
                  />
                )}
              </div>
            );
          })}
        </div>
      ))}
      {(slot.maxItems === undefined || entries.length < slot.maxItems) ? (
        <button type="button" className="btn btn-sm btn-ghost" onClick={add}>
          {t('Add entry')}
        </button>
      ) : null}
      {vio ? <span className="dke-violation">{vio.message}</span> : null}
    </div>
  );
}

/**
 * Per-row overrides for the dynamic slots — the suffixes come from the LIVE bound rows
 * ('kurzform:DEL-01', 'subtitle:DEL-01') or cards ('takeaway_override:<id>'), with the
 * register default as placeholder, so the operator sees what they would be overriding.
 */
function DynamicOverrides({ slot, slide, draft, violations, bound, setSlot, saveDraft }: {
  slot: SlotDef;
  slide: DeckSlide;
  draft: Record<string, unknown>;
  violations: SlotViolation[];
  bound: SlideBound | undefined;
  setSlot: (key: string, value: unknown) => void;
  saveDraft: () => void;
}) {
  const t = useT();

  let targets: Array<{ suffix: string; name: string; placeholder: string }> = [];
  if (slide.slide_type === 'arbeitsergebnisse_tabelle' && bound?.tabelle) {
    const rows = bound.tabelle.pages.flat();
    targets = rows.map((r) => ({
      suffix: r.ref,
      name: r.ref,
      placeholder: slot.key === 'kurzform:' ? r.kurzform : r.subtitle,
    }));
  } else if (slide.slide_type === 'referenzen_karten' && bound?.referenzen) {
    targets = bound.referenzen.cards.map((c) => ({
      suffix: String(c.id),
      name: c.slug,
      placeholder: c.takeaway,
    }));
  }
  if (targets.length === 0) return null;

  return (
    <details className="dke-slot">
      <summary className="form-label" style={{ cursor: 'pointer' }}>
        {t(slot.label)} · {targets.length}
      </summary>
      {targets.map((tg) => {
        const key = `${slot.key}${tg.suffix}`;
        const value = String(draft[key] ?? '');
        const over = slot.maxChars !== undefined && value.trim().length > slot.maxChars;
        const vio = violationFor(violations, key);
        return (
          <div className="dke-slot" key={key} style={{ marginTop: 6 }}>
            <SlotHead label={tg.name} count={value.trim().length} max={slot.maxChars} over={over} />
            <textarea
              className={`form-textarea${over ? ' dke-over' : ''}`}
              rows={2}
              value={value}
              placeholder={tg.placeholder}
              onChange={(e) => setSlot(key, e.target.value === '' ? undefined : e.target.value)}
              onBlur={saveDraft}
            />
            {vio ? <span className="dke-violation">{vio.message}</span> : null}
          </div>
        );
      })}
    </details>
  );
}

/** The read-only register block: what feeds this slide, and where to edit it. */
function BoundInfo({ bound, loading }: {
  bound: SlideBound | undefined;
  loading: boolean;
}) {
  const t = useT();
  let summary = '';
  if (bound?.gantt) summary = t('{n} deliverables on the time track', { n: bound.gantt.rows.length });
  else if (bound?.tabelle) summary = t('{n} deliverables across {p} pages', { n: bound.tabelle.pages.flat().length, p: bound.tabelle.pages.length });
  else if (bound?.mesh) summary = t('{r} requirements · {d} deliverables · {e} edges', { r: bound.mesh.groups.reduce((n, g) => n + g.reqRefs.length, 0), d: bound.mesh.dels.length, e: bound.mesh.edges.length });
  else if (bound?.referenzen) summary = t('{n} reference cards selected', { n: bound.referenzen.cards.length });
  else if (bound?.team) summary = t('{n} people from the library', { n: bound.team.persons.length });
  else if (bound?.anhang) summary = t('{n} requirements across {p} pages', { n: bound.anhang.pages.reduce((x, p) => x + p.rows.length, 0), p: bound.anhang.pages.length });
  else summary = loading ? t('Loading register data …') : t('No register data yet for this slide.');

  return (
    <div className="dke-bound">
      <span className="dke-bound-chip">{t('From the register')}</span>
      <span className="dke-bound-note">{summary}</span>
      <span className="dke-bound-note">
        {t('This block is filled live from the register — edit deliverables, requirements and dates there.')}
      </span>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Image framing — drag the photograph inside its frame, zoom with the slider
// ─────────────────────────────────────────────────────────────────────────────

/** The box aspect (w/h) an image slot renders into — what the framer must mimic. */
function frameAspectFor(slide: DeckSlide, slotKey: string, draft: Record<string, unknown>): number {
  const sx = typeof draft.split_x === 'number' ? draft.split_x : null;
  switch (slide.slide_type) {
    case 'auftrag_split': return (1280 - (sx ?? 755)) / 720;
    case 'naechste_schritte_split': return (1280 - (sx ?? 640)) / 720;
    case 'bild_split': {
      const x = sx ?? 640;
      return (draft.image_side === 'left' ? x : 1280 - x) / 720;
    }
    default: return 1280 / 720; // cover, dividers: full bleed
  }
}

function ImageFramer({ src, slotKey, draft, aspect, onChange, onCommit }: {
  src: string;
  slotKey: string;
  draft: Record<string, unknown>;
  aspect: number;
  onChange: (fx: number, fy: number, zoom: number) => void;
  onCommit: () => void;
}) {
  const t = useT();
  const f = imageFrameOf(draft, slotKey);
  const frameRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; fx: number; fy: number } | null>(null);
  const W = 340;
  const H = Math.round(W / aspect);
  const z = Math.min(3, Math.max(1, f.zoom / 100));

  // Dragging moves the focal point against the drag direction (you drag the picture, not the
  // frame); the sensitivity follows the overflow so a full drag across the frame spans 0–100 %.
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, fx: f.fx, fy: f.fy };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.x;
    const dy = e.clientY - drag.current.y;
    const nfx = Math.min(100, Math.max(0, drag.current.fx - (dx / W) * 100));
    const nfy = Math.min(100, Math.max(0, drag.current.fy - (dy / H) * 100));
    onChange(Math.round(nfx), Math.round(nfy), f.zoom);
  };
  const onPointerUp = () => { if (drag.current) { drag.current = null; onCommit(); } };

  return (
    <div className="dke-framer">
      <div
        ref={frameRef}
        className="dke-framer-frame"
        style={{ width: W, height: H, cursor: 'grab' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            objectPosition: `${f.fx}% ${f.fy}%`,
            transform: z !== 1 ? `scale(${z})` : undefined,
            transformOrigin: `${f.fx}% ${f.fy}%`,
            pointerEvents: 'none',
          }}
        />
      </div>
      <div className="dke-framer-row">
        <span className="dke-hint">{t('Drag to move · zoom')}</span>
        <input
          type="range" min={100} max={300} step={5} value={f.zoom}
          onChange={(e) => onChange(f.fx, f.fy, Number(e.target.value))}
          onPointerUp={onCommit}
          onKeyUp={onCommit}
        />
        <span className="mono dke-hint">{f.zoom}%</span>
        <button
          type="button" className="btn btn-sm btn-ghost"
          onClick={() => { onChange(50, 50, 100); onCommit(); }}
        >{t('Reset')}</button>
      </div>
    </div>
  );
}
